import FieldTypes from '../constants/FieldTypes';

/**
 * Auto-shrink helper
 *
 * Pure calculator for the autoShrink / maxLines / maxLength feature. Knows nothing about
 * React or the page, so the on-screen renderer and the PDF exporter can ask it the same
 * question and get the same answer. That is what makes an export match what people saw.
 */

//Defaults applied when the feature is switched on but a value is not supplied
const DEFAULT_MAX_LINES = 2;
const DEFAULT_MAX_LENGTH = 100;

/**
 * The smallest font we will ever produce. Without a floor a long value in a small box would
 * shrink toward nothing. Below this we stop and let the ellipsis absorb the rest.
 */
const MIN_FONT_SIZE = 6;

//How tall a line is as a multiple of the font size, matching the browser default
const DEFAULT_LINE_HEIGHT_RATIO = 1.2;

/**
 * The line height, in whole pixels, that a given font size draws at.
 *
 * One rounded expression rather than a Math.round spelled out at each call site, because the
 * editor node and the display node have to agree to the pixel. Where they differed by one the
 * text visibly jumped as focus moved in and out, and a height measured from the editor did not
 * describe the box the reader ends up looking at.
 *
 * @param {Number} fontSize
 * @returns {Number}
 */
export const getLineHeightPixels = (fontSize) => Math.round(fontSize * DEFAULT_LINE_HEIGHT_RATIO);

//One offscreen measuring surface shared by every field, created on first use
let sharedContext;

//Word widths, keyed by font and word. The same words get measured repeatedly while typing.
const measurementCache = new Map();

const MEASUREMENT_CACHE_LIMIT = 5000;

/**
 * Stop counting lines once we are far past any sensible limit. Pasting an essay should not
 * cost more work than pasting a sentence.
 */
const MAX_COUNTED_LINES = 200;

/**
 * The single field-type gate for this feature.
 *
 * Returns null unless autoShrink is explicitly true AND the field is a text or textarea
 * field. Every part of the feature checks this first, which is what keeps number, uniqueId,
 * table cell, collection row and chart usages of the shared components unaffected.
 *
 * @param {Object} params
 * @param {String} params.type //Field type
 * @param {Boolean} params.autoShrink
 * @param {Number} params.maxLines
 * @param {Number} params.maxLength
 * @returns {Object|null}
 */
export const resolveAutoShrinkConfig = ({ type, autoShrink, maxLines, maxLength }) => {

  /**
   * Testing for exactly true, rather than merely truthy, so a stray string left on an older
   * document can not switch the feature on.
   */
  if (autoShrink !== true) return null;

  /**
   * Text and textarea only. TextField also renders number and uniqueId fields, so without
   * this check those would be swept up in it.
   */
  if (type !== FieldTypes.text && type !== FieldTypes.textarea) return null;

  return {
    maxLines: Number.isFinite(maxLines) && maxLines > 0 ? Math.floor(maxLines) : DEFAULT_MAX_LINES,
    maxLength: Number.isFinite(maxLength) && maxLength > 0 ? Math.floor(maxLength) : DEFAULT_MAX_LENGTH,
  };

};

/**
 * Get the shared offscreen measuring context.
 *
 * Returns null when there is no canvas available, which is the case in jsdom and on the
 * server, so every caller has a defined way to cope instead of throwing.
 *
 * @returns {Object|null}
 */
const getMeasurementContext = () => {

  if (sharedContext !== undefined) return sharedContext;

  try {

    //Never added to the document, so nothing is painted and no layout work is triggered
    const canvas = document.createElement('canvas');

    sharedContext = canvas.getContext('2d');

  } catch (e) {

    //Record null so we do not retry on every call
    sharedContext = null;

  }

  return sharedContext;

};

/**
 * Measure one word, in pixels, at a given font.
 *
 * @param {Object} context
 * @param {String} font //CSS font shorthand
 * @param {String} word
 * @returns {Number}
 */
const measureWord = (context, font, word) => {

  /**
   * Font is part of the key because the same word is a different width in bold 12px than in
   * italic 8px.
   */
  const cacheKey = `${font}|${word}`;

  if (measurementCache.has(cacheKey)) return measurementCache.get(cacheKey);

  context.font = font;

  const width = context.measureText(word).width;

  if (measurementCache.size < MEASUREMENT_CACHE_LIMIT) measurementCache.set(cacheKey, width);

  return width;

};

/**
 * Count the lines one paragraph needs, using the same greedy rule a word processor uses.
 *
 * @param {Object} params
 * @returns {Number}
 */
const countLinesForParagraph = ({ words, availableWidth, font, context }) => {

  /**
   * An empty paragraph, which is what a blank line between two paragraphs looks like, still
   * occupies one line on screen.
   */
  if (words.length === 0) return 1;

  let lines = 1;
  let currentLineWidth = 0;

  const spaceWidth = measureWord(context, font, ' ');

  for (let i = 0; i < words.length; i++) {

    const wordWidth = measureWord(context, font, words[i]);

    /**
     * A word too wide to fit on any line, such as a long URL. The display sets
     * overflow-wrap: break-word, so the browser splits it across several lines. Counting it
     * as a single line would badly under-estimate and ellipsize text away for no reason.
     */
    if (wordWidth > availableWidth) {

      if (currentLineWidth > 0) lines += 1;

      //Round up, because a word needing 2.3 lines occupies 3 of them
      const brokenLines = Math.ceil(wordWidth / availableWidth);

      lines += (brokenLines - 1);

      //The leftover tail of the broken word starts the next line
      currentLineWidth = wordWidth % availableWidth;

    } else {

      //Every word after the first on a line also needs a leading space
      const widthWithSpace = currentLineWidth === 0 ? wordWidth : currentLineWidth + spaceWidth + wordWidth;

      if (widthWithSpace <= availableWidth) {

        currentLineWidth = widthWithSpace;

      } else {

        lines += 1;
        currentLineWidth = wordWidth;

      }

    }

  }

  return lines;

};

/**
 * Count the lines the whole value needs at a given font.
 *
 * @param {Object} params
 * @returns {Number}
 */
const countWrappedLines = ({ text, availableWidth, font, context }) => {

  /**
   * Split on real line breaks first. Multiline values contain them and the display honours
   * them via white-space: pre-line, so ignoring them would under-count badly: a five line
   * address measured as a single paragraph can look like it fits on two.
   */
  const paragraphs = text.split(/\r?\n/);

  let total = 0;

  for (let i = 0; i < paragraphs.length; i++) {

    /**
     * Runs of spaces and tabs collapse, which is exactly what white-space: pre-line does, so
     * this count agrees with what the browser will draw.
     */
    const words = paragraphs[i].split(/[ \t]+/).filter(Boolean);

    total += countLinesForParagraph({ words, availableWidth, font, context });

    if (total > MAX_COUNTED_LINES) return total;

  }

  return total;

};

/**
 * Apply the same text-transform the CSS will apply, before measuring. Uppercase text is
 * noticeably wider than the same words in mixed case, so measuring the untransformed value
 * would under-estimate and push text into the ellipsis.
 *
 * @param {String} text
 * @param {String} textTransform
 * @returns {String}
 */
const applyTextTransform = (text, textTransform) => {

  if (textTransform === 'uppercase') return text.toUpperCase();

  if (textTransform === 'lowercase') return text.toLowerCase();

  if (textTransform === 'capitalize') return text.replace(/\b\w/g, (c) => c.toUpperCase());

  return text;

};

/**
 * Work out how much to shrink the font, as a multiplier where 1 means no change at all.
 *
 * <----------------------------NOTICE------------------------->
 * Everything here is measured against the saved design numbers, never the live size on
 * screen. The editor scales the field box but not the font size, and the exporter does the
 * reverse, so a factor fitted from on-screen pixels would not reproduce in the export.
 * Working from the persisted numbers is what makes the two agree.
 *
 * @param {Object} params
 * @returns {Number}
 */
export const calculateShrinkFactor = ({
  text,
  designWidth,
  designHeight,
  baseFontSize,
  maxLines,
  maxLength,
  fontFamily,
  fontWeight,
  fontStyle,
  textTransform,
  lineHeightRatio = DEFAULT_LINE_HEIGHT_RATIO,
  padding = 0,
  borderWidth = 0,
  reservedHeight = 0,
  availableHeight: measuredHeight,
  ignoreHeight = false,
}) => {

  //Value can arrive as a string, a number, null or undefined
  const safeText = String(text === null || text === undefined ? '' : text);

  if (!safeText) return 1;

  /**
   * The maxLength rule. Only the first maxLength characters are ever measured, so once the
   * value is longer than that the input to this calculation stops changing and the size
   * settles. Typing is never blocked. Deleting back under the limit shortens this slice
   * again and the size recovers, with nothing cached to go stale.
   */
  const measuredText = applyTextTransform(safeText.slice(0, maxLength), textTransform);

  /**
   * <----------------------------NOTICE------------------------->
   * Shrinking can not remove a hard line break.
   *
   * Making the font smaller fits more characters onto a line, so it reduces the number of
   * lines the text WRAPS onto. It has no effect at all on line breaks the user typed, because
   * those are honoured at every size.
   *
   * So when the typed breaks alone already exceed maxLines, no font size can satisfy the
   * limit and the search would run all the way to the floor for nothing: the reader would get
   * the same two visible lines, just unreadably small. Leaving the font alone and letting the
   * clamp ellipsize shows exactly the same content at a legible size.
   *
   * This only applies to breaks the user typed. A single long paragraph that wraps onto too
   * many lines is still shrunk normally, because there shrinking genuinely reveals more text.
   */
  const hardLineCount = measuredText.split(/\r?\n/).length;

  if (hardLineCount > maxLines) return 1;

  /**
   * Padding and border both eat into the box from both sides.
   *
   * The border has to be subtracted because the display node is box-sizing: border-box, so
   * its border sits inside the designed width and height rather than outside them. Leaving it
   * out told the calculation there was more room than the browser would give it, and on a
   * short field that was enough to let the text spill past the border.
   */
  const availableWidth = Math.max(1, designWidth - (padding * 2) - (borderWidth * 2));

  /**
   * The room left for text once the title, padding and border are out of the way.
   *
   * A measured height is used whenever the caller has one, because deriving it means guessing
   * at the title row, the wrapper margin and the grid item's own border and padding, and
   * those guesses came out several pixels short in practice. Too generous a guess picks a
   * font that does not really fit, which is how text ended up over the border.
   *
   * Only the height is ever measured, never the width. Heights are not scaled: rowHeight is a
   * fixed pixel value and clientHeight reports layout rather than painted size, so it reads
   * the same on screen as in an export. Widths are scaled to the viewport, so measuring one
   * would give the two different answers and the export would stop matching the screen.
   */
  const availableHeight = Number.isFinite(measuredHeight)
    ? Math.max(1, measuredHeight)
    : Math.max(1, designHeight - (padding * 2) - (borderWidth * 2) - reservedHeight);

  /**
   * <----------------------------NOTICE------------------------->
   * The box cannot hold even one line at the smallest font we are willing to use.
   *
   * When that is true the height rule is unsatisfiable: no size on the way down to the floor
   * passes it, so honouring it sends the search straight to MIN_FONT_SIZE and renders a short
   * value at 6px for no benefit at all. The reader gets unreadable text in a box that was
   * never going to fit it either way.
   *
   * This is reachable on PDF pages in particular, where fields are drawn to line up with a
   * scanned form at heights around 25px while the file's own padding and border already
   * account for 26px between them, leaving a content box of zero.
   *
   * Same reasoning as the hard line break guard above: when shrinking cannot rescue the fit,
   * do not shrink. Width and maxLines are both still enforced below, so a value that genuinely
   * does not fit across the field is still reduced as it always was.
   */
  /**
   * ignoreHeight is how auto-height switches the height rule off entirely.
   *
   * When the box is allowed to grow, the height is an outcome rather than a constraint, so the
   * font is decided by maxLines, maxLength and the width alone. Leaving the rule in would set
   * up a loop: growing the box loosens it, the font creeps back up, the taller text overflows
   * again, and the field grows a little more on every single blur.
   */
  const heightIsUnsatisfiable = ignoreHeight || availableHeight < Math.round(MIN_FONT_SIZE * lineHeightRatio);

  const context = getMeasurementContext();

  /**
   * No canvas, which is the case in jsdom and on the server. Render at the normal size
   * rather than throwing. The CSS clamp still limits the visible lines.
   */
  if (!context) return 1;

  /**
   * The starting point is the font size the user chose. We only ever go down from here, so a
   * field whose text already fits is left completely alone.
   */
  const startSize = Math.floor(baseFontSize);

  if (!Number.isFinite(startSize) || startSize <= MIN_FONT_SIZE) return 1;

  if (!Number.isFinite(availableWidth) || !Number.isFinite(availableHeight)) return 1;

  /**
   * CSS font shorthand order: style, weight, size, family. Matching the rendered element
   * matters, because bold text is wider than normal text at the same size.
   */
  const buildFont = (size) => `${fontStyle || 'normal'} ${fontWeight || 'normal'} ${size}px ${fontFamily || 'sans-serif'}`;

  const fitsAtSize = (size) => {

    const lines = countWrappedLines({
      text: measuredText,
      availableWidth,
      font: buildFont(size),
      context,
    });

    //Never more lines than we are allowed to show
    if (lines > maxLines) return false;

    /**
     * The height rule is skipped entirely when no size could satisfy it. See the notice on
     * heightIsUnsatisfiable above. Width and maxLines have both already been applied by this
     * point, so the size returned is still the largest one whose text fits across the field.
     */
    if (heightIsUnsatisfiable) return true;

    /**
     * Those lines stacked up must also fit the height left after the title.
     *
     * Rounded, because that is what the rendered line-height does. Comparing against the
     * unrounded product let this approve a size the browser then drew a fraction taller on
     * every line, which accumulates into a visible overflow.
     */
    return (lines * Math.round(size * lineHeightRatio)) <= availableHeight;

  };

  //The common case: it already fits at the chosen size, so change nothing
  if (fitsAtSize(startSize)) return 1;

  /**
   * Binary search the font size. Each round halves what is left, so a 6 to 12 range settles
   * in about five rounds rather than seven one by one tries.
   */
  let low = MIN_FONT_SIZE;
  let high = startSize;

  /**
   * The best size known to work. Starts at the floor so that if nothing fits we still return
   * something readable and let the ellipsis handle the overflow.
   */
  let bestSize = MIN_FONT_SIZE;

  while (low <= high) {

    const middle = Math.floor((low + high) / 2);

    if (fitsAtSize(middle)) {

      bestSize = middle;
      low = middle + 1;

    } else {

      high = middle - 1;

    }

  }

  /**
   * Returning a ratio rather than a pixel size is what lets the screen and the exporter each
   * apply it in their own scale and still agree.
   */
  return bestSize / startSize;

};

/**
 * How many whole lines actually fit inside the field.
 *
 * maxLines is a line count with no knowledge of the box it sits in, and -webkit-line-clamp
 * limits lines rather than height, so between them nothing stops a clamped block being taller
 * than the field. When that happens the field's own overflow rule cuts it at a pixel, which
 * lands in the middle of a row of letters.
 *
 * Capping maxLines with this is what guarantees the ellipsis falls at the end of a whole line.
 *
 * @param {Object} params
 * @param {Number} params.maxLines //The limit the designer set
 * @param {Number} params.availableHeight //Measured room for text, preferred when known
 * @param {Number} params.designHeight //The field's drawn height in pixels
 * @param {Number} params.padding
 * @param {Number} params.borderWidth
 * @param {Number} params.reservedHeight //Room taken by the field title
 * @param {Number} params.lineHeightPixels //The line-height the CSS will actually use
 * @returns {Number}
 */
export const getFittingMaxLines = ({
  maxLines,
  availableHeight: measuredHeight,
  designHeight,
  padding = 0,
  borderWidth = 0,
  reservedHeight = 0,
  lineHeightPixels,
}) => {

  //A real line height is the one thing there is no way to work around
  if (!Number.isFinite(lineHeightPixels) || lineHeightPixels <= 0) return maxLines;

  /**
   * Prefer a measured height whenever the caller has one.
   *
   * Deriving the room for text from designHeight means guessing at everything between the
   * two: the title row, the wrapper margin, and the grid item's own border and padding. Those
   * guesses were a couple of pixels out in practice, which is enough to clip the descenders
   * off the last line. A caller that can read the real box should, and this falls back to the
   * arithmetic for the render paths that cannot.
   */
  let availableHeight = measuredHeight;

  //Fall back to working it out from the drawn height when nothing has been measured
  if (!Number.isFinite(availableHeight) && Number.isFinite(designHeight)) {
    availableHeight = designHeight - (padding * 2) - (borderWidth * 2) - reservedHeight;
  }

  //Nothing usable to measure against, so leave the limit exactly as the designer set it
  if (!Number.isFinite(availableHeight)) return maxLines;

  //Floor, because a line that only half fits is precisely the cut this exists to prevent
  const linesThatFit = Math.floor(availableHeight / lineHeightPixels);

  /**
   * Never zero. A field drawn too short for even one line still shows that line, clipped,
   * because an empty box tells the reader nothing at all. This is the one case that can still
   * be cut part way, and it takes a field shorter than its own padding, border, title and a
   * single line of text combined.
   */
  if (linesThatFit < 1) return 1;

  //The designer's limit still applies. This only ever tightens it, never loosens it
  return Math.min(maxLines, linesThatFit);

};

/**
 * The CSS that limits the visible lines and adds the ellipsis.
 *
 * No JavaScript produces the ellipsis. The browser enforces this using real layout, in the
 * app and in the exporter alike, which is what guarantees text can not spill out of the box.
 *
 * The line limit itself is only emitted for a real number. Auto-height passes Infinity,
 * meaning "as many lines as the value needs", and -webkit-line-clamp: Infinity is not a valid
 * declaration: the browser would drop it, which happens to be the wanted result, but it leaves
 * an invalid rule in the stylesheet. Leaving it out says the same thing properly, and the box
 * is then the only thing limiting the text.
 *
 * @param {Object} params
 * @param {Number} params.maxLines
 * @returns {String}
 */
export const getAutoShrinkClampStyles = ({ maxLines }) => `
  display: -webkit-box;
  -webkit-box-orient: vertical;
  ${Number.isFinite(maxLines) ? `-webkit-line-clamp: ${maxLines};` : ''}
  overflow: hidden;
  white-space: pre-line;
  overflow-wrap: break-word;
`;

export default resolveAutoShrinkConfig;
